import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiFetch, ApiError } from "../lib/api";
import type { LoginResponse, MeResponse } from "../lib/types";

type SessionStatus = "checking" | "authenticated" | "unauthenticated" | "unavailable";
type SessionProbeResult =
  | { status: "authenticated"; user: MeResponse }
  | { status: "unauthenticated" }
  | { status: "error"; message: string };

type LoginInput = {
  email: string;
  password: string;
  mfaCode?: string;
};

type SessionContextValue = {
  status: SessionStatus;
  user: MeResponse | null;
  loading: boolean;
  refreshingSession: boolean;
  error: string | null;
  login: (input: LoginInput) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

function getErrorMessage(error: unknown): string {
  return error instanceof Error && error.message.trim() ? error.message.trim() : "Unknown error";
}

function formatLogoutFailureMessage(error: unknown): string {
  const message = getErrorMessage(error);
  return `Logout failed. You are still signed in. ${message === "Unknown error" ? "Retry logout." : message}`;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("checking");
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshingSession, setRefreshingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef<SessionStatus>("checking");
  const userRef = useRef<MeResponse | null>(null);
  const sessionProbeRequestSeqRef = useRef(0);

  const applySessionSnapshot = useCallback(
    (nextStatus: SessionStatus, nextUser: MeResponse | null, nextError: string | null) => {
      statusRef.current = nextStatus;
      userRef.current = nextUser;
      setStatus(nextStatus);
      setUser(nextUser);
      setError(nextError);
    },
    [],
  );

  const probeSession = useCallback(async (): Promise<SessionProbeResult> => {
    try {
      const payload = await apiFetch<MeResponse>("/auth/me");
      return { status: "authenticated", user: payload };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        return { status: "unauthenticated" };
      }
      return { status: "error", message: getErrorMessage(err) };
    }
  }, []);

  const invalidateSessionProbes = useCallback((options: { clearRefreshing?: boolean } = {}) => {
    sessionProbeRequestSeqRef.current += 1;
    if (options.clearRefreshing) {
      setRefreshingSession(false);
    }
  }, []);

  const probeSessionLatest = useCallback(async (): Promise<SessionProbeResult | null> => {
    const requestId = ++sessionProbeRequestSeqRef.current;
    setRefreshingSession(true);
    try {
      const result = await probeSession();
      if (requestId !== sessionProbeRequestSeqRef.current) {
        return null;
      }
      return result;
    } finally {
      if (requestId === sessionProbeRequestSeqRef.current) {
        setRefreshingSession(false);
      }
    }
  }, [probeSession]);

  const refreshSession = useCallback(async () => {
    setError(null);
    const result = await probeSessionLatest();
    if (!result) {
      return;
    }
    if (result.status === "authenticated") {
      applySessionSnapshot("authenticated", result.user, null);
      return;
    }
    if (result.status === "unauthenticated") {
      applySessionSnapshot("unauthenticated", null, null);
      return;
    }
    const message = result.message;
    if (statusRef.current === "authenticated") {
      setStatus("authenticated");
      setUser(userRef.current);
      setError(message);
      return;
    }
    if (statusRef.current === "checking" || statusRef.current === "unavailable") {
      applySessionSnapshot("unavailable", null, message);
      return;
    }
    applySessionSnapshot("unauthenticated", null, message);
  }, [applySessionSnapshot, probeSessionLatest]);

  useEffect(() => {
    refreshSession().catch(() => undefined);
  }, [refreshSession]);

  const login = useCallback(async ({ email, password, mfaCode }: LoginInput) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, mfa_code: mfaCode || undefined }),
      });
      if (result.ok) {
        invalidateSessionProbes();
        applySessionSnapshot("authenticated", userRef.current, null);
        await refreshSession();
      }
      return result;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [applySessionSnapshot, invalidateSessionProbes, refreshSession]);

  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    const previousStatus = statusRef.current;
    const previousUser = userRef.current;
    try {
      await apiFetch("/auth/logout", { method: "POST" });
      invalidateSessionProbes({ clearRefreshing: true });
      applySessionSnapshot("unauthenticated", null, null);
      return;
    } catch (err) {
      const logoutError = new Error(formatLogoutFailureMessage(err));
      const probeResult = await probeSessionLatest();
      if (!probeResult) {
        throw logoutError;
      }
      if (probeResult.status === "unauthenticated") {
        applySessionSnapshot("unauthenticated", null, null);
        return;
      }
      if (probeResult.status === "authenticated") {
        applySessionSnapshot("authenticated", probeResult.user, null);
        throw logoutError;
      }
      applySessionSnapshot(previousStatus, previousUser, null);
      throw logoutError;
    } finally {
      setLoading(false);
    }
  }, [applySessionSnapshot, invalidateSessionProbes, probeSessionLatest]);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user,
      loading,
      refreshingSession,
      error,
      login,
      logout,
      refreshSession,
      clearError: () => setError(null),
    }),
    [error, loading, login, logout, refreshSession, refreshingSession, status, user],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
}
